import { useEffect, useMemo, useState } from "react";
import { ShieldCheck, Zap, MessageCircle, BadgePercent } from "lucide-react";
import usePageMeta from "../../hooks/usePageMeta";
import useJsonLd from "../../hooks/useJsonLd";
import { api } from "../../lib/api";
import { SearchBox, StickySearchCTA } from "../../components/b2c/SearchBox";
import { AirlineCard, RouteCard, SectionHeading, FaqBlock } from "../../components/b2c/Blocks";
import { BannerStrip } from "../../components/BannerStrip";
import { useCopy } from "../../context/CopyContext";

const IMG_HERO = "https://images.unsplash.com/photo-1560142454-b818f9dd9fe1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const TRUST_ICONS = [BadgePercent, Zap, MessageCircle, ShieldCheck];

export default function FlightsHome() {
  const t = useCopy();
  usePageMeta(
    "Farevault by Sellvago — Cheap Airline Tickets from Pakistan",
    "Search discounted flights from Karachi, Lahore & Islamabad to Dubai, Jeddah, Doha, London and more. Compare Emirates, PIA, Qatar Airways fares instantly."
  );
  const [airlines, setAirlines] = useState([]);
  const [routes, setRoutes] = useState([]);
  const trust = [1, 2, 3, 4].map((i) => ({ icon: TRUST_ICONS[i - 1], t: t(`b2c.trust${i}`) }));
  const homeFaqs = [1, 2, 3, 4].map((i) => ({ q: t(`b2c.faq${i}.q`), a: t(`b2c.faq${i}.a`) }));

  useEffect(() => {
    api.get("/airlines").then((r) => setAirlines(r.data)).catch(() => {});
    api.get("/routes").then((r) => setRoutes(r.data)).catch(() => {});
  }, []);

  const pakistanOffers = useMemo(() => {
    const PK = ["KHI", "LHE", "ISB", "PEW", "MUX", "LYP", "UET", "SKZ", "GWD", "RYK"];
    return routes.filter((r) => PK.includes(r.origin_code) || PK.includes(r.dest_code));
  }, [routes]);

  const itemListLd = useMemo(() => {
    if (!pakistanOffers.length) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Farevault — discounted flights from Pakistan",
      "description": "Curated Sellvago Farevault flight discount offers from Pakistan airports (KHI, LHE, ISB and more).",
      "numberOfItems": pakistanOffers.length,
      "itemListElement": pakistanOffers.map((r, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "url": `https://sellvago.com/flights/${r.slug}`,
        "name": `${r.origin_city} to ${r.dest_city}`,
        "item": {
          "@type": "Trip",
          "name": `${r.origin_city} (${r.origin_code}) to ${r.dest_city} (${r.dest_code}) flights`,
          "url": `https://sellvago.com/flights/${r.slug}`,
          "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": "PKR",
            "lowPrice": r.price_from,
            "url": `https://sellvago.com/flights/${r.slug}`,
          },
        },
      })),
    };
  }, [pakistanOffers]);

  useJsonLd("farevault-pakistan-offers", itemListLd);

  return (
    <div>
      <section className="relative bg-[#040B16]">
        <div className="absolute inset-0 h-[440px] sm:h-[400px]">
          <img src={IMG_HERO} alt="Airplane flying at sunset" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#040B16]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-10">
          <div className="max-w-2xl fade-up">
            <p className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-[#00E5FF] border border-[#00E5FF]/30 bg-[#00E5FF]/5 px-3 py-1 rounded-sm mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FF66] fv-tick" /> Wholesale fares · live
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
              {t("b2c.hero.title")}
            </h1>
            <p className="mt-4 text-slate-300 text-base md:text-lg max-w-lg">
              {t("b2c.hero.subtitle")}
            </p>
          </div>
          <div className="mt-8">
            <SearchBox page="/flights" />
          </div>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {trust.map((b) => (
              <div key={b.t} className="flex items-center gap-2 bg-[#0B1527] rounded-sm px-3 py-2.5 border border-[#1E293B]">
                <b.icon className="w-4 h-4 text-[#00E5FF] shrink-0" />
                <span className="text-xs font-semibold text-slate-300">{b.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14">
        <BannerStrip placement="b2c_home_hero" className="sm:grid-cols-2 lg:grid-cols-3" />
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-20">
        <SectionHeading
          overline={t("b2c.air.overline")}
          title={t("b2c.air.title")}
          subtitle={t("b2c.air.subtitle")}
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="airlines-grid">
          {airlines.map((a) => <AirlineCard key={a.slug} airline={a} />)}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-20">
        <SectionHeading
          overline={t("b2c.rou.overline")}
          title={t("b2c.rou.title")}
          subtitle={t("b2c.rou.subtitle")}
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="routes-grid">
          {routes.map((r) => <RouteCard key={r.slug} route={r} />)}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-20">
        <SectionHeading overline={t("b2c.faq.overline")} title={t("b2c.faq.title")} />
        <FaqBlock faqs={homeFaqs} testId="home-faq-accordion" />
      </section>

      <StickySearchCTA />
    </div>
  );
}
